import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, CheckCircle, Save } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';

const LABELS = {
  loan_amount: 'Loan amount ($)', interest_rate: 'Interest rate', points: 'Origination points', term_months: 'Term (months)',
  amortization: 'Amortization', payment_structure: 'Payment structure', ltv: 'LTV (%)', prepayment_penalty: 'Prepayment penalty',
  fees: 'Lender / closing fees', recourse: 'Recourse', closing_timeline: 'Estimated closing', conditions: 'Conditions',
};
const STATUS_CLS = { verified: 'bg-green-100 text-green-800', preliminary: 'bg-amber-100 text-amber-800', calculated: 'bg-amber-100 text-amber-800', pending: 'bg-red-100 text-red-800' };

// Edit / approve / download a term sheet. Verified = from approved lender program; Preliminary = broker estimate; Pending = unknown.
export const TermSheetEditor = ({ termSheet, onChanged }) => {
  const [fields, setFields] = useState(termSheet.fields || {});
  const [notes, setNotes] = useState(termSheet.terms?.notes || '');
  const [saving, setSaving] = useState(false);
  const locked = ['sent', 'signed'].includes(termSheet.status);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const setField = (k, patch) => setFields((f) => ({ ...f, [k]: { ...(f[k] || {}), ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/term-sheets/${termSheet.term_sheet_id}`, { fields, notes });
      toast.success('Term sheet saved (back to draft — approve again before sending)');
      onChanged?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    try {
      const res = await api.post(`/term-sheets/${termSheet.term_sheet_id}/approve`);
      toast.success(`Approved. ${res.data.pending_terms.length} term(s) still pending lender confirmation.`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approval failed');
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/term-sheets/${termSheet.term_sheet_id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Could not generate PDF');
    }
  };

  const counts = Object.values(fields).reduce((a, f) => ({ ...a, [f.status]: (a[f.status] || 0) + 1 }), {});

  return (
    <div className="space-y-4" data-testid="term-sheet-editor">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Badge className={STATUS_CLS.verified}>{counts.verified || 0} verified</Badge>
        <Badge className={STATUS_CLS.preliminary}>{(counts.preliminary || 0) + (counts.calculated || 0)} preliminary / calculated</Badge>
        <Badge className={STATUS_CLS.pending}>{counts.pending || 0} pending</Badge>
        <span className="text-muted-foreground">Status: <strong className="capitalize">{termSheet.status}</strong></span>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {Object.keys(LABELS).map((k) => {
          const f = fields[k] || { value: null, status: 'pending' };
          return (
            <div key={k} className="space-y-1" data-testid={`ts-field-${k}`}>
              <div className="flex items-center justify-between">
                <label className="text-xs text-muted-foreground">{LABELS[k]}</label>
                <Badge className={`${STATUS_CLS[f.status]} text-[10px]`} data-testid={`ts-field-${k}-status`}>{f.status}</Badge>
              </div>
              <div className="flex gap-1">
                <Input
                  value={f.value ?? ''}
                  placeholder="Pending lender confirmation"
                  disabled={locked}
                  onChange={(e) => setField(k, { value: e.target.value, status: e.target.value ? (f.status === 'pending' ? 'preliminary' : f.status) : 'pending' })}
                  className="h-8 text-sm"
                  data-testid={`ts-field-${k}-input`}
                />
                {!locked && f.value && (
                  <Select value={f.status} onValueChange={(v) => setField(k, { status: v })}>
                    <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`ts-field-${k}-status-select`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="verified">Verified</SelectItem>
                      <SelectItem value="preliminary">Preliminary</SelectItem>
                      <SelectItem value="calculated">Calculated</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {fields.axe_fees_total && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm grid sm:grid-cols-3 gap-2" data-testid="ts-axe-fees">
          <div><p className="text-xs text-muted-foreground">AXE origination fee</p><p className="font-medium" data-testid="ts-axe-origination">{fields.axe_origination_fee?.value}</p></div>
          <div><p className="text-xs text-muted-foreground">AXE processing fee</p><p className="font-medium" data-testid="ts-axe-processing">{fields.axe_processing_fee?.value}</p></div>
          <div><p className="text-xs text-muted-foreground">Total AXE fees (auto-recalculated from loan amount)</p><p className="font-semibold" data-testid="ts-axe-total">{fields.axe_fees_total?.value}</p></div>
        </div>
      )}
      <div>
        <label className="text-xs text-muted-foreground">Notes (printed on the term sheet)</label>
        <Textarea value={notes} disabled={locked} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="ts-notes-input" />
      </div>
      <div className="flex flex-wrap gap-2">
        {!locked && (
          <Button size="sm" variant="outline" onClick={save} disabled={saving} data-testid="ts-save-btn">
            <Save className="h-4 w-4 mr-1" /> Save changes
          </Button>
        )}
        {termSheet.status === 'draft' && (
          <Button size="sm" onClick={approve} data-testid="ts-approve-btn">
            <CheckCircle className="h-4 w-4 mr-1" /> Approve for sending
          </Button>
        )}
        <Button size="sm" variant="secondary" onClick={downloadPdf} data-testid="ts-pdf-btn">
          <Download className="h-4 w-4 mr-1" /> Branded PDF
        </Button>
      </div>
      {termSheet.status === 'draft' && <p className="text-xs text-muted-foreground">Sending is disabled until the term sheet is approved. Pending terms print as “Pending lender confirmation”; preliminary terms are labeled as estimates.</p>}
      {!backendUrl && <p className="text-xs text-red-600">Backend URL missing</p>}
    </div>
  );
};

export default TermSheetEditor;
